import { productsClient } from "@core/clients";
import { QueryFunctionContext, useInfiniteQuery, UseInfiniteQueryOptions } from "@tanstack/react-query";
import { PRODUCTS_QUERY_CACHE_EXPIRE_TIME_MS } from "../../config/constansts";
import { ProductsQueryParams, ProductResults } from "@core/types";

type QueryParams = ProductsQueryParams;
type PageParam = ProductsQueryParams;

type QueryKey = ["products", QueryParams];
type Options = UseInfiniteQueryOptions<
  ProductResults | undefined,
  any,
  ProductResults | undefined,
  ProductResults | undefined,
  QueryKey
>;

const getInitialPageParam = (params: QueryParams): PageParam => ({
  ...params,
  pageNumber: params.pageNumber ?? "1",
});

const useGetProductsInfiniteQuery = (config: { options?: Options; params: QueryParams }) => {
  const initialPageParam = getInitialPageParam(config.params);

  return useInfiniteQuery<ProductResults | undefined, any, ProductResults | undefined, QueryKey>({
    queryKey: ["products", config.params],
    queryFn: async (queryContext: QueryFunctionContext<QueryKey, PageParam>) => {
      const { pageParam = initialPageParam, queryKey } = queryContext;
      const configParams = queryKey[1];
      return await productsClient.getProducts({
        ...configParams,
        additionalProductFilter: pageParam.additionalProductFilter,
        pageNumber: String(pageParam.pageNumber ?? "1"),
        categoryId: pageParam.categoryId,
        saleType: pageParam.saleType,
        productIds: pageParam.productIds,
        zoneId: config.params.zoneId,
      });
    },
    getNextPageParam: (lastRes: ProductResults | undefined, allPages: (ProductResults | undefined)[]) => {
      const perPage = config.params?.pageSize ? Number(config.params.pageSize) : 10;
      const itemsAmount = allPages.reduce((pageA, pageB) => pageA + (pageB?.products?.length ?? 0), 0);

      if (lastRes?.products?.length === perPage) {
        return {
          ...config.params,
          pageNumber: Math.ceil(itemsAmount / perPage) + 1,
        };
      }

      // Writing this explicitly to indicate that if we
      // return 'undefined', then no query will be made
      return undefined;
    },
    cacheTime: PRODUCTS_QUERY_CACHE_EXPIRE_TIME_MS,
    staleTime: PRODUCTS_QUERY_CACHE_EXPIRE_TIME_MS,
    ...config.options,
  });
};

export default useGetProductsInfiniteQuery;
